import { useIonLoading } from '@ionic/react'
import { AxiosError, AxiosHeaders } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { CANCELLED, COUNTRY, EVENTS } from '../const/constants'
import { useAppContext } from '../context/AppContext'
import {
  COMPLETE_INPUT,
  SAVE_LINE_VALIDATION_RESPONSE,
  newProspectoCompleted,
  saveDocumentId,
  saveLineNumber,
  saveLineValidationResponse
} from '../redux/actions'
import {
  savePortabilityDocument,
  signActivationDocument,
  signPortabilityDocument
} from '../services/api/dig-business-sales'
import { cancelProcedures, saveIntegrationLogs } from '../services/api/dig-documents-manager'
import { getInfoLinea, validatePosSim } from '../services/api/mobile-activation-service'
import { createDocumentNom, getDatosNimPresusp, signAndProcess, validaCliente } from '../services/api/nominatividad'
import { saveReferido, validateDuplicatedReferido } from '../services/api/porta-creacion/index'
import { reSendPinCustomer, validateLineNumber } from '../services/api/porta-qualification'
import { imageWeightControl } from '../utils/utils'
import useMessages from './useMessages'

const useRequests = (storeForm?: string) => {
  const formData = storeForm && useSelector((state: any) => state[storeForm])

  const storedProcedure = useSelector((state: any) => state.storedProcedure)
  const OBDResponse = useSelector((state: any) => state.OBDResponse)
  const documentId = useSelector((state: any) => state.documentId)
  const lineValidationResponse = useSelector((state: any) => state.lineValidationResponse)
  const documentBack = useSelector((state: any) => state.photoBack)
  const documentFront = useSelector((state: any) => state.photoFront)
  const photoClient = useSelector((state: any) => state.photoClient)
  const clientPin = useSelector((state: any) => state.clientPin)
  const idNominativityForm = useSelector((state: any) => state.idNominativityForm)
  const nomLineForm = useSelector((state: any) => state.nomLineForm)
  const addressForm = useSelector((state: any) => state.addressForm)
  const initializedDate = useSelector((state: any) => state.initializedDate)

  const [present, dismiss] = useIonLoading()
  const history = useHistory()
  const { user, settings, setBlockNavigation }: any = useAppContext()
  const { toastMessage } = useMessages()
  const dispatch = useDispatch()

  const validity = Object.values({ ...formData }).some((value) => !value)

  const handleSubmitProspectos = async () => {
    present({
      message: `Cargando referido. <br/> Esperá por favor.`
    })
    let errorType
    const saveProspectoRequest = {
      apellidoProspecto: formData.apellido,
      document: formData.documento || '',
      idAgente: user.extCode,
      mailProspecto: formData.email,
      negocio: formData.negocio,
      nombreProspecto: formData.nombre,
      razonSocial: user.name,
      telefonoKiosco: user.posNumber,
      telefonoKioscoPersonal: user.userName,
      telefonoProspecto: formData.telefono
    }
    try {
      if (settings.country === COUNTRY.AR) {
        const validateProspectoRequest = {
          documento: formData.documento,
          nim: formData.telefono,
          producto: formData.negocio
        }
        const validateReferido = await validateDuplicatedReferido(validateProspectoRequest)
        errorType = 1
        if (validateReferido.returnValue !== '0')
          throw new Error('El documento ingresado ya fué referido anteriormente.')
      }
      const savedProspecto = await saveReferido(saveProspectoRequest, settings.language)
      toastMessage('¡Referido cargado con éxito!', 3000, 'bottom', 'success')
      return savedProspecto
    } catch (error: any) {
      if (errorType === 1) toastMessage(error.message, 3000, 'bottom', 'error')
      else toastMessage('No se pudo guardar el referido', 3000, 'bottom', 'error')
    } finally {
      dismiss()
      dispatch(newProspectoCompleted())
      history.push('/procedures/referidos')
      setBlockNavigation(false)
    }
  }

  const chipAndLineValidations = async () => {
    const body = {
      documentNumber: '',
      documentType: '',
      line: formData.lineNumber
    }
    if (settings.country === COUNTRY.PY) {
      body.documentNumber = OBDResponse.identityNumber
      body.documentType = OBDResponse.documentType
    }
    try {
      const portaQualiResponse = await validateLineNumber(body)
      const getInfoLineaResponse = await getInfoLinea(formData.cardNumber)
      dismiss()
      if (getInfoLineaResponse.activated)
        return toastMessage('Esta SIM ya se encuentra activa.', 3000, 'bottom', 'error')
      dispatch(saveLineValidationResponse({ ...portaQualiResponse, ...getInfoLineaResponse }))
      return {
        portable: portaQualiResponse.portable,
        activated: getInfoLineaResponse.activated,
        ocmId: portaQualiResponse.ocmId,
        rangeType: portaQualiResponse.tipoRango,
        cellularNumber: getInfoLineaResponse.cellularNumber
      }
    } catch (error) {
      dismiss()
      toastMessage('La línea no es portable.', 3000, 'bottom', 'error')
    } finally {
      dismiss()
    }
  }

  const savePortabilityDocuments = async (lineInfo: Record<string, string>) => {
    const body = {
      customer: {
        birthDate: OBDResponse.data.birthDate,
        country: OBDResponse.data.nationality,
        documentNumber: OBDResponse.identityNumber,
        documentType: OBDResponse.documentType,
        name: OBDResponse.data.name,
        lastName: OBDResponse.data.surname,
        gender: OBDResponse.data.gender,
        email: formData.emailOptional
      },
      phone: {
        simNumber: formData.cardNumber,
        specialCellularNumber: formData.lineNumber,
        businessType: formData.serviceType,
        cellularNumber: lineInfo.cellularNumber,
        ocmId: lineInfo.ocmId,
        rangeType: lineInfo.rangeType
      },
      processInitializedDate: initializedDate,
      user: user.posNumber,
      posNumber: formData.posNumberProcedure,
      validationId: OBDResponse.flowId,
      profile: user.profile
    }

    try {
      const documentsResponse = await savePortabilityDocument(body)

      if (!documentsResponse.data.success)
        throw new AxiosError(
          documentsResponse.data.error,
          documentsResponse.status.toString(),
          { url: '/businessSales/kiosk/portability/documents', headers: new AxiosHeaders() },
          body,
          documentsResponse.data
        )
      dispatch(saveDocumentId(documentsResponse.data.response.didId))
      return documentsResponse.data
    } catch (error: any) {
      if (error instanceof AxiosError)
        await saveIntegrationLogs({ ...error, event: EVENTS.savePortability, user: user.posNumber })
      toastMessage('No se pudo guardar el trámite.', 3000, 'bottom', 'error')
    }
  }

  const reSendPin = async () => {
    present({
      message: `Reenviando PIN. <br/> Esperá por favor.`
    })
    const body = {
      documentNumber: '',
      documentType: '',
      line: formData.lineNumber || storedProcedure.specialCellularNumber
    }
    if (settings.country === COUNTRY.PY) {
      body.documentNumber = OBDResponse.identityNumber || storedProcedure.identificationNumber
      body.documentType = OBDResponse.documentType || storedProcedure.identificationType
    }
    try {
      const response = await reSendPinCustomer(body)
      dismiss()
      if (response.toString() === 'PIN NO GENERADO') throw new Error(response.result)
      toastMessage('El PIN se reenvió correctamente.', 3000, 'bottom', 'success')
      return response
    } catch (error) {
      dismiss()
      toastMessage('No se pudo reenviar el PIN, intentá nuevamente.', 3000, 'bottom', 'error')
    }
  }

  const simValidation = async () => {
    present({
      message: `Comprobando datos. <br/> Esperá por favor.`
    })
    try {
      const infoLineaResponse = await getInfoLinea(formData.cardNumber)
      if (infoLineaResponse.activated) throw new Error('Esta SIM ya se encuentra activa.')
      dispatch(saveLineValidationResponse(infoLineaResponse))
      const validatPosSimResponse = await validatePosSim({ pos: user.posActivator, sim: formData.cardNumber })
      if (validatPosSimResponse.status !== 0) throw new Error(validatPosSimResponse.error.message)

      dismiss()
      return {
        success: !infoLineaResponse.activated && validatPosSimResponse.status === 0,
        cellularNumber: infoLineaResponse.cellularNumber,
        simNumber: formData.cardNumber
      }
    } catch (error: any) {
      dismiss()
      toastMessage(error.message, 3000, 'bottom', 'error')
      return {
        success: false
      }
    }
  }

  const signDocumentAndActivate = async (base64Signature: string) => {
    present({
      message: `Finalizando el trámite. <br/> Esperá por favor.`
    })
    const request = {
      didId: documentId,
      signature: base64Signature,
      user: user.posNumber
    }
    try {
      const signatureResponse = await signActivationDocument(request)
      if (!signatureResponse.data.success)
        throw new AxiosError(
          signatureResponse.data.error,
          signatureResponse.status.toString(),
          { url: '/businessSales/kiosk/activation/signature', headers: new AxiosHeaders() },
          { ...request, signature: request.signature.slice(0, 10) },
          signatureResponse.data
        )
      dispatch(saveLineNumber(signatureResponse.data.response.cellularNumber))
      dismiss()
      return signatureResponse.data
    } catch (error: any) {
      dismiss()
      if (error instanceof AxiosError)
        await saveIntegrationLogs({
          ...error,
          didId: parseInt(request.didId),
          event: EVENTS.signActivation,
          user: user.posNumber
        })
      if (error.code === '500') toastMessage('Error de servidor, intentá más tarde', 3000, 'bottom', 'error')
    }
  }

  const signDocumentPortability = async (base64Signature: string) => {
    present({
      message: `Finalizando el trámite. <br/> Esperá por favor.`
    })
    const request = {
      didId: documentId,
      signature: base64Signature,
      pin: clientPin,
      user: user.posNumber
    }
    try {
      const signatureResponse = await signPortabilityDocument(request)
      if (!signatureResponse.data.success)
        throw new AxiosError(
          signatureResponse.data.error,
          signatureResponse.status.toString(),
          { url: '/businessSales/kiosk/portability/signature', headers: new AxiosHeaders() },
          { ...request, signature: request.signature.slice(0, 10) },
          signatureResponse.data
        )
      dispatch(saveLineNumber(signatureResponse.data.response.cellularNumber))
      dismiss()
      return signatureResponse.data
    } catch (error: any) {
      dismiss()
      if (error instanceof AxiosError)
        await saveIntegrationLogs({
          ...error,
          didId: parseInt(request.didId),
          event: EVENTS.signPortability,
          user: user.posNumber
        })
      if (error.code === '500') toastMessage('Error de servidor, intentá más tarde', 3000, 'bottom', 'error')
    }
  }

  const cancelProcedure = async (idDocument: number) => {
    present({
      message: `Cancelando trámite. <br/> Esperá por favor.`
    })
    const request = {
      didId: idDocument,
      digDocumentations: { state: CANCELLED },
      digStatesHistory: { state: CANCELLED },
      digEventsHistory: [{ event: CANCELLED }],
      user: user.posNumber
    }
    try {
      const documentsResponse = await cancelProcedures(request)
      if (!documentsResponse.data.success)
        throw new AxiosError(
          documentsResponse.data.error,
          documentsResponse.status.toString(),
          { url: '/manager/models/documentations', headers: new AxiosHeaders() },
          request,
          documentsResponse.data
        )
      toastMessage('Trámite cancelado con éxito', 3000, 'bottom', 'success')
    } catch (error: any) {
      if (error instanceof AxiosError)
        await saveIntegrationLogs({
          ...error,
          didId: request.didId,
          event: EVENTS.cancelDocument,
          user: user.posNumber
        })
      toastMessage('No se pudo cancelar el trámite, intentá más tarde', 3000, 'bottom', 'error')
    } finally {
      dismiss()
    }
  }

  const validateLine = async (line: string) => {
    present({
      message: `Validando línea. <br/> Esperá por favor.`
    })
    try {
      const response = await getDatosNimPresusp({
        cellularNumber: line,
        flagIngTmk: false
      })
      if (!response.flagLevantaPresusp) throw new Error()
      dispatch({ type: SAVE_LINE_VALIDATION_RESPONSE, payload: response })
      return response
    } catch (error: any) {
      toastMessage(`Esta línea no puede ser activada.\nIntentá con otro Chip. (1)`, 3000, 'bottom', 'error')
    } finally {
      dismiss()
    }
  }

  const searchClient = async (documentNumber: string) => {
    present({
      message: `Buscando cliente. <br/> Esperá por favor.`
    })
    try {
      const response = await validaCliente(documentNumber)
      dispatch({
        type: COMPLETE_INPUT,
        payload: {
          storeForm: 'idNominativityForm',
          name: 'identificationNumber',
          value: response.srfIdentificationNumber
        }
      })
      if (!response.isClt) return response
      dispatch({
        type: COMPLETE_INPUT,
        payload: {
          storeForm: 'idNominativityForm',
          name: 'lastName',
          value: response.srfClientLastName
        }
      })
      dispatch({
        type: COMPLETE_INPUT,
        payload: {
          storeForm: 'idNominativityForm',
          name: 'names',
          value: response.srfClientFirstName
        }
      })
      dispatch({
        type: COMPLETE_INPUT,
        payload: {
          storeForm: 'idNominativityForm',
          name: 'birthDate',
          value: response.srfBirthDate
        }
      })
      dispatch({
        type: COMPLETE_INPUT,
        payload: {
          storeForm: 'idNominativityForm',
          name: 'sex',
          value: response.srfSex
        }
      })
      return response
    } catch (error: any) {
      toastMessage(`No se encontraron datos para ese cliente.`, 3000, 'bottom', 'error')
    } finally {
      dismiss()
    }
  }

  const saveNominativity = async () => {
    present({
      message: `Guardando trámite. <br/> Esperá por favor.`
    })
    const [frontPicture, backPicture, clientPicture] = await imageWeightControl(
      documentFront,
      documentBack,
      photoClient
    )
    const data = {
      createDocument: {
        application: 'tucarpeta.com',
        attachments: [
          {
            contents: backPicture,
            contentType: 'image/jpg',
            name: 'DNI_BACK'
          },
          {
            contents: clientPicture,
            contentType: 'image/jpg',
            name: 'SELFIE'
          }
        ],
        contents: frontPicture,
        contentType: 'image/jpg',
        metadata: {
          claro: {
            birthDate: idNominativityForm.birthDate,
            limitDate: idNominativityForm.limitDate,
            country: idNominativityForm.nationality,
            documentNumber: idNominativityForm.identificationNumber,
            documentType: 'EXT',
            passport: idNominativityForm.passport.toUpperCase(),
            email: nomLineForm.emailOptional,
            names: idNominativityForm.names.trim(),
            lastName: idNominativityForm.lastName.trim(),
            gender: idNominativityForm.sex
          },
          nom: {
            phone: {
              lineNumber: nomLineForm.lineNumber,
              simNumber: lineValidationResponse.escEsnHexa,
              status: 'EN CURSO'
            },
            address: {
              accAddressCity: addressForm.accAddressCity,
              addressStreet: addressForm.addressStreet,
              addressFlat: addressForm.addressFlat || '',
              addressFloor: addressForm.addressFloor || '',
              geuDescription: addressForm.geuDescription,
              geuId: addressForm.geuId,
              addressNumber: addressForm.addressNumber
            },
            creationDateUtc: initializedDate,
            circuit: 'APPK'
          }
        },
        name: `${idNominativityForm.identificationNumber} ${idNominativityForm.lastName} ${idNominativityForm.names}`
      },
      userName: user.userName
    }
    try {
      const response = await createDocumentNom(data)
      dispatch(saveDocumentId(response.documentId))
      return response
    } catch (error) {
      toastMessage('Error de servidor, intentá más tarde', 3000, 'bottom', 'error')
    } finally {
      dismiss()
    }
  }

  const processPreactivation = async (clientSign: string) => {
    present({
      message: `Finalizando el trámite. <br/> Esperá por favor.`
    })
    const data = {
      id: documentId,
      sign: clientSign,
      userName: user.userName
    }
    try {
      return await signAndProcess(data)
    } catch (error) {
      toastMessage('Error de servidor, intentá más tarde', 3000, 'bottom', 'error')
    } finally {
      dismiss()
    }
  }

  return {
    formData,
    validity,
    handleSubmitProspectos,
    chipAndLineValidations,
    savePortabilityDocuments,
    signDocumentPortability,
    reSendPin,
    simValidation,
    signDocumentAndActivate,
    cancelProcedure,
    validateLine,
    searchClient,
    saveNominativity,
    processPreactivation
  }
}

export default useRequests
